import {
  Anchor,
  AnchorProps,
  CSSProperties,
  ElementProps,
} from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { forwardRef } from 'react';

import { ZorroActionIcon as ActionIcon, ZorroIcon as Icon } from '../Icon';
import classNames from './Anchor.module.scss';

export interface IAnchorProps
  extends AnchorProps, ElementProps<'a', keyof AnchorProps> {
  isMasked?: boolean;
  shouldShowExternalIcon?: boolean;
  externalLinkSize?: string;
  iconProps?: CSSProperties;
}

// 🧠 DO NOT EXPORT! DO NOT USE OUTSIDE THE `design-system` library! USE `Link` instead
export const ZorroAnchor = forwardRef<HTMLAnchorElement, IAnchorProps>(
  (
    {
      // eslint-disable-next-line id-length
      c = 'zorroIris.9',
      target,
      children,
      isMasked,
      shouldShowExternalIcon = true,
      externalLinkSize = '1.25rem',
      iconProps,
      ...props
    },
    ref
  ) => {
    const isExternalLink = target === '_blank';

    const anchorClassNames = [
      classNames.root ?? null,
      isMasked ? 'fs-mask' : null,
    ].filter((className): className is string => Boolean(className));

    return (
      <Anchor
        ref={ref}
        component="a"
        c={c}
        target={target}
        classNames={{
          ...classNames,
          root: anchorClassNames.join(' '),
        }}
        data-external={isExternalLink}
        {...props}
      >
        {children}
        {isExternalLink && shouldShowExternalIcon && (
          <ActionIcon size="sm" isTransparent>
            <Icon
              icon={IconExternalLink}
              size={externalLinkSize}
              style={{
                flexShrink: 0,
                '--icon-external-link-color':
                  iconProps?.color ?? 'var(--mantine-color-zorroIris-9)',
              }}
            />
          </ActionIcon>
        )}
      </Anchor>
    );
  }
);

ZorroAnchor.displayName = 'ZorroAnchor';
